import { useCallback, useEffect, useRef, useState } from 'react';
import {
  hasForceRotateDisplayResource,
  isLoadableDisplayPreviewUrl,
  isOssDirectDisplayUrl,
  isSameDisplayImageResource,
  isSignedDisplayUrl,
  msUntilViewPathExpires,
} from '@/utils/imageDisplayAccess';
import { acquireManagedViewLoadSlot } from '@/utils/managedImageViewLoadQueue';
import {
  noteViewLoadFailure,
  noteViewLoadSuccess,
  waitForViewLoadCooldown,
} from '@/utils/viewLoadRateLimitGuard';
import {
  isManagedViewImageLoaded,
  markManagedViewImageLoaded,
} from '@/utils/managedViewImageLoadCache';
import { findScrollableParent } from '@/components/ipe/ipeLazyVisibility';

const DEFAULT_ROOT_MARGIN = '480px';

function isPublicDirectSrc(url) {
  const s = String(url || '').trim();
  if (!s) return false;
  if (s.startsWith('data:') || s.startsWith('blob:') || s.startsWith('indexeddb://')) return true;
  if (s.startsWith('/images/') || s.startsWith('/assets/')) return true;
  return false;
}

/** 与 imageDisplayAccess 策略一致：HMAC / OSS 预签名 / 本地绝对 uploads / 外链；裸 canonical 与裸 OSS 不可 */
export function isLoadableImgSrc(url) {
  const s = String(url || '').trim();
  if (!s) return false;
  if (isPublicDirectSrc(s)) return true;
  return isLoadableDisplayPreviewUrl(s);
}

function canReuseLoadedViewUrl(previousUrl, nextUrl, activeSrc) {
  const prev = String(previousUrl || '').trim();
  const next = String(nextUrl || '').trim();
  const current = String(activeSrc || '').trim();
  if (!next || !current) return false;
  if (!isSameDisplayImageResource(prev || current, next)) return false;
  // invalidate / 同路径覆盖后：即使未过期也须加载新签名，不能复用旧 src
  if (hasForceRotateDisplayResource(next) || hasForceRotateDisplayResource(prev || current)) {
    return false;
  }
  if (!isManagedViewImageLoaded(prev || current)) return false;
  if (isSignedDisplayUrl(current) && msUntilViewPathExpires(current) <= 0) return false;
  return true;
}

/**
 * OSS/CDN 预签名或已加载过的地址：跳过 HMAC 队列。
 * 避免虚拟列表可见格卡在 waitingForLoad；HMAC `/view` 仍走队列防 429。
 */
function shouldBypassLoadQueue(url) {
  const s = String(url || '').trim();
  if (!s) return false;
  if (isManagedViewImageLoaded(s)) return true;
  if (isPublicDirectSrc(s)) return true;
  if (isOssDirectDisplayUrl(s)) return true;
  return false;
}

function resolveObserverRoot(explicitRoot, element) {
  if (explicitRoot) return explicitRoot;
  return findScrollableParent(element) || null;
}

/**
 * 受管 signed view 缩略图：视口懒加载 + HMAC view 并发队列（C2）。
 * 限流时仅冷却对应域，等在途请求完成后再按 FIFO 继续。
 * @param {string} viewUrl - 已签名的展示地址
 * @param {{ eager?: boolean, rootMargin?: string, root?: Element|null }} [options]
 */
export function useManagedViewImageSrc(viewUrl, options = {}) {
  const eager = Boolean(options.eager);
  const rootMargin = options.rootMargin ?? DEFAULT_ROOT_MARGIN;
  const root = options.root ?? null;

  const [activeSrc, setActiveSrc] = useState(() => {
    const url = String(viewUrl || '').trim();
    if (url && (isManagedViewImageLoaded(url) || isPublicDirectSrc(url))) {
      return url;
    }
    // OSS/CDN 预签名直链：eager 时首帧直出；否则交给下方视口懒加载，避免整页并发拉满
    if (url && eager && isOssDirectDisplayUrl(url)) {
      return url;
    }
    return '';
  });
  const elementRef = useRef(null);
  const releaseRef = useRef(null);
  const abortRef = useRef(null);
  const requestIdRef = useRef(0);
  const prevViewUrlRef = useRef('');
  const activeSrcRef = useRef(activeSrc);
  activeSrcRef.current = activeSrc;

  const releaseSlot = useCallback(() => {
    releaseRef.current?.();
    releaseRef.current = null;
  }, []);

  const abortPendingAcquire = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  const beginLoad = useCallback(async (url, requestId) => {
    if (shouldBypassLoadQueue(url)) {
      if (requestIdRef.current === requestId) setActiveSrc(url);
      return;
    }
    await waitForViewLoadCooldown(url);
    if (requestIdRef.current !== requestId) return;

    abortPendingAcquire();
    const ac = new AbortController();
    abortRef.current = ac;

    const slot = await acquireManagedViewLoadSlot(url, { signal: ac.signal });
    if (slot.cancelled || requestIdRef.current !== requestId) {
      slot.release();
      return;
    }
    releaseRef.current = slot.release;
    setActiveSrc(url);
  }, [abortPendingAcquire]);

  useEffect(() => {
    const url = String(viewUrl || '').trim();
    const previousUrl = prevViewUrlRef.current;
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    abortPendingAcquire();

    if (!url || !isLoadableImgSrc(url)) {
      setActiveSrc('');
      releaseSlot();
      prevViewUrlRef.current = url;
      return undefined;
    }

    if (canReuseLoadedViewUrl(previousUrl, url, activeSrcRef.current)) {
      markManagedViewImageLoaded(url);
      prevViewUrlRef.current = url;
      // 上一轮 effect cleanup 可能已排队 setActiveSrc('')；复用路径必须重新确认 src，
      // 否则会卡在 pending 占位（缩略图被加载动画盖住，预览另开一路仍正常）。
      const keep = String(activeSrcRef.current || '').trim();
      setActiveSrc(keep || url);
      return undefined;
    }

    // 同资源已在展示：勿先清空再排队（虚拟列表 remount / 签名刷新会概率性永久白底）
    const sameShowing = Boolean(
      activeSrcRef.current
      && isSameDisplayImageResource(activeSrcRef.current, url)
      && !hasForceRotateDisplayResource(url),
    );
    if (sameShowing && isManagedViewImageLoaded(activeSrcRef.current || url)) {
      prevViewUrlRef.current = url;
      setActiveSrc(url);
      markManagedViewImageLoaded(url);
      return undefined;
    }

    prevViewUrlRef.current = url;
    if (!sameShowing) {
      setActiveSrc('');
    }
    releaseSlot();

    if (eager) {
      void beginLoad(url, requestId);
      return () => {
        requestIdRef.current += 1;
        abortPendingAcquire();
        releaseSlot();
        // 勿在 cleanup 清空已展示的 src：依赖变化时若 canReuse 早退，
        // cleanup 的 setActiveSrc('') 会盖过复用路径，造成概率性永久占位。
      };
    }

    const target = elementRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') {
      void beginLoad(url, requestId);
      return () => {
        requestIdRef.current += 1;
        abortPendingAcquire();
        releaseSlot();
      };
    }

    let cancelled = false;
    const obsRoot = resolveObserverRoot(root, target);
    const observer = new IntersectionObserver(
      (entries) => {
        if (cancelled || !entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        void beginLoad(url, requestId);
      },
      { root: obsRoot, rootMargin, threshold: 0.01 },
    );
    observer.observe(target);

    return () => {
      cancelled = true;
      observer.disconnect();
      requestIdRef.current += 1;
      abortPendingAcquire();
      releaseSlot();
    };
  }, [viewUrl, eager, root, rootMargin, beginLoad, releaseSlot, abortPendingAcquire]);

  const onLoad = useCallback(() => {
    const url = String(viewUrl || '').trim();
    if (url) {
      markManagedViewImageLoaded(url);
      noteViewLoadSuccess(url);
    }
    releaseSlot();
  }, [releaseSlot, viewUrl]);

  const onError = useCallback(() => {
    // 限流属正常节流：按 URL 域冷却，其它域/直链可继续；不当成 UI 错误提示
    noteViewLoadFailure(String(viewUrl || '').trim());
    releaseSlot();
  }, [releaseSlot, viewUrl]);

  return {
    src: activeSrc,
    elementRef,
    onLoad,
    onError,
    pending: Boolean(viewUrl && isLoadableImgSrc(viewUrl) && !activeSrc),
  };
}
